<template>
    <div class="event-edit">
        <div class="event-edit__header">
            <div class="event-edit__header-inner">
                <div class="event-edit__back" @click="backToOverview">
                    <GI name="global_previous" :ws="isPhone ? '30' : '32'" :hs="isPhone ? '14' : '16'"></GI>
                    <span>{{ langJson('Back to overview') }}</span>
                </div>
                <EventActions v-if="event" :event="event" :before-submit="flushForm" @changed="onChanged"></EventActions>
            </div>
        </div>

        <div v-if="event" class="event-edit__center">
            <EventStepNav v-model="step" class="event-edit__nav" :steps="steps" @input="changeStep"></EventStepNav>

            <div class="event-edit__main">
                <div class="event-edit__intro">
                    <h2>{{ langJson(current.heading) }}</h2>
                    <span>{{ langJson(current.intro) }}</span>
                </div>
                <div v-if="readOnlyNotice" class="event-edit__notice">{{ langJson(readOnlyNotice) }}</div>

                <EventBasicsForm v-if="step === 'basics'" ref="form" :event="event" @saved="onSaved" @status="onStatus"></EventBasicsForm>
                <EventStripePanel v-else-if="step === 'stripe'" :event="event"></EventStripePanel>
                <EventTicketingForm v-else-if="step === 'ticketing'" ref="form" :event="event" @saved="onSaved" @status="onStatus"></EventTicketingForm>
                <EventPromotionPanel v-else-if="step === 'promotion'" :event="event"></EventPromotionPanel>
                <EventVerificationPanel v-else-if="step === 'verification'" :event="event"></EventVerificationPanel>

                <div class="event-edit__footer">
                    <span :class="['event-edit__save', `event-edit__save--${saveState}`]">{{ saveText }}</span>
                    <BTN v-if="next" type="active" bh="48" @handle="changeStep(next.key)">{{ langJson('Next') }}: {{ langJson(next.label) }}</BTN>
                </div>
            </div>
        </div>
        <div v-else-if="error" class="event-edit__error">{{ error }}</div>
    </div>
</template>
<script>
import organizerApi from '@/api/organizer'
import { apiErrorMessage, navigate } from '@/assets/js/organizerEvent'

const STEPS = [
    { key: 'basics', label: 'Basics', icon: 'edit_basics_active', heading: 'Start with the basics', intro: 'Tell people what your event is, when and where it happens.' },
    { key: 'stripe', label: 'Stripe', icon: 'edit_treaty_active', heading: 'Connect with Stripe', intro: 'Ticket revenue is paid out to your Stripe account.' },
    { key: 'ticketing', label: 'Ticketing', icon: 'edit_rewards_active', heading: 'Ticketing', intro: 'Set your currency, transferability and ticket categories.' },
    { key: 'promotion', label: 'Promotion', icon: 'edit_promotion_active', heading: 'Promotion', intro: 'Get ready to promote your event.', afterApproval: true },
    { key: 'verification', label: 'Ticket Verification', icon: 'edit_link_active', heading: 'Manage Ticket Verification', intro: 'Create links for the staff who scan tickets at the door.', afterApproval: true },
]

export default {
    name: 'EventEditPage',
    data() {
        return {
            event: null,
            error: '',
            step: STEPS.some((s) => s.key === this.$route.query.step) ? this.$route.query.step : 'basics',
            saveState: 'idle',
            saveError: '',
        }
    },
    computed: {
        approved() {
            return this.event && ['approved', 'live', 'ended'].includes(this.event.status)
        },
        steps() {
            const checklist = (this.event && this.event.checklist) || {}
            return STEPS.map((s) => ({
                ...s,
                done: checklist[s.key],
                locked: s.afterApproval && !this.approved,
                lockedHint: 'Available once your event is approved',
            }))
        },
        current() {
            return STEPS.find((s) => s.key === this.step)
        },
        next() {
            const visible = this.steps.filter((s) => !s.locked)
            const index = visible.findIndex((s) => s.key === this.step)
            return visible[index + 1] || null
        },
        readOnlyNotice() {
            // Edit locks only matter on the steps that edit the event itself.
            if (!this.event || !['basics', 'ticketing'].includes(this.step)) return ''
            return {
                in_review: 'Your event is in review, so it can’t be edited right now.',
                approved: this.event.deployStatus === 'deploying' ? 'Your event is launching — this takes about a minute.' : 'Your event is approved. Changing anything sends it back for review.',
                live: 'Your event is live — only the description and images can be changed.',
                ended: 'This event has ended and can no longer be edited.',
            }[this.event.status]
        },
        saveText() {
            return {
                idle: '',
                saving: this.langJson('Saving…'),
                saved: this.langJson('Your changes have been saved'),
                error: this.saveError,
            }[this.saveState]
        },
    },
    created() {
        this.$store.commit('saveHeadView', { create: false })
        this.load()
    },
    watch: {
        '$route.query.step'(step) {
            if (step && step !== this.step) this.step = step
        },
    },
    methods: {
        async load() {
            try {
                const res = await organizerApi.getEvent(this.$route.query.id)
                this.event = res.data
                if (this.steps.find((s) => s.key === this.step).locked) this.step = 'basics'
            } catch (err) {
                this.error = apiErrorMessage(err, this.langJson('Could not load this event'))
            }
        },
        changeStep(key) {
            this.flushForm()
            this.saveState = 'idle'
            this.step = key
            navigate(this, { query: { ...this.$route.query, step: key } }, { replace: true })
            const top = document.getElementById('view_main')
            if (top) top.scrollTop = 0
        },
        flushForm() {
            return this.$refs.form ? this.$refs.form.flush() : Promise.resolve()
        },
        backToOverview() {
            this.flushForm()
            this.$router.push({ path: '/brand/events/overview', query: { id: this.event ? this.event.id : this.$route.query.id } })
        },
        // Submit / withdraw / launch changed the status — locks and banners follow it.
        onChanged(event) {
            this.event = event
        },
        onSaved(event) {
            // Keep the form's own state; only refresh what the page reads (status, checklist, locks).
            this.event = { ...this.event, status: event.status, checklist: event.checklist, editableFields: event.editableFields, tiersEditable: event.tiersEditable, title: event.title, currency: event.currency }
        },
        onStatus(state, message = '') {
            this.saveState = state
            this.saveError = message
        },
    },
}
</script>
<style lang="scss" scoped>
.event-edit {
    padding-bottom: 64px;

    &__header {
        border-bottom: 1px solid var(--border_color);
    }
    &__header-inner {
        max-width: var(--max_width);
        margin: 0 auto;
        min-height: 116px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 16px;
    }
    &__back {
        display: flex;
        align-items: center;
        gap: 16px;
        cursor: pointer;
        font-family: 'Inter';
        font-weight: 700;
        font-size: 20px;
        color: var(--font_deep_color);
    }
    &__center {
        max-width: var(--max_width);
        margin: 32px auto 0;
        display: flex;
        align-items: flex-start;
        gap: 32px;
    }
    &__nav {
        width: 296px;
        flex-shrink: 0;
        position: sticky;
        top: 24px;
    }
    &__main {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 32px;
    }
    &__intro {
        display: flex;
        flex-direction: column;
        gap: 8px;

        h2 {
            font-family: 'Cabinet Grotesk';
            font-weight: 800;
            font-size: 40px;
            line-height: 48px;
            color: var(--font_deep_color);
        }
        span {
            font-family: 'Lato';
            font-size: 16px;
            color: var(--font_modif_color);
        }
    }
    &__notice {
        padding: 16px 24px;
        border-radius: 24px;
        background: #fff6e5;
        color: #b54708;
        font-family: 'Lato';
        font-size: 14px;
    }
    &__footer {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        gap: 24px;
    }
    &__save {
        font-family: 'Lato';
        font-size: 14px;
        color: var(--font_deep_color);

        &--saving {
            color: var(--font_modif_color);
        }
        &--error {
            color: var(--danger_color);
        }
    }
    &__error {
        padding: 64px 0;
        text-align: center;
        font-family: 'Lato';
        color: var(--font_modif_color);
    }
}

@media only screen and (max-width: 1080px) {
    .event-edit {
        &__header-inner {
            min-height: 72px;
            padding: 0 16px;
        }
        &__back {
            font-size: 16px;
        }
        &__center {
            flex-direction: column;
            align-items: stretch;
            gap: 16px;
            margin-top: 16px;
            padding: 0 16px;
        }
        &__nav {
            width: 100%;
            position: static;
        }
        &__main {
            gap: 16px;
        }
        &__intro h2 {
            font-size: 28px;
            line-height: 34px;
        }
        &__footer {
            flex-direction: column-reverse;
            align-items: stretch;
            gap: 12px;
            text-align: center;
        }
    }
}
</style>
